import React, { useState, useEffect } from 'react';
import FitTable from '../components/FitTable';
import PlayerName from '../components/PlayerName';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

interface BreakdownEvent {
  kind: 'round' | 'major' | 'round_bonus';
  date: string;
  course: string;
  position: number | null;
  points: number;
}

interface StandingRecord {
  player_id: number;
  first_name: string;
  last_name: string;
  total_round_points: number;
  total_round_team_points: number;
  total_tournament_points: number;
  total_tournament_team_points: number;
  total_points: number;
  breakdown?: BreakdownEvent[];
}

interface RegFinishesRecord {
  player_id: number;
  first_name: string;
  last_name: string;
  regular_season_starts: number;
  rank_1_finishes: number;
  rank_2_finishes: number;
  rank_3_finishes: number;
  rank_4_finishes: number;
  rank_5_finishes: number;
  rank_6_finishes: number;
  rank_7_finishes: number;
  dq_finishes: number;
  recent?: { date: string; pos: number | string }[]; // { date, pos } per finish, chronological (oldest → newest); pos is a placement or 'DQ'
}

interface MajorFinishRow {
  player_id: number;
  first_name: string;
  last_name: string;
  majors_played: number;
  finishes: number[]; // index 0 = 1st-place count
  recent?: { date: string; pos: number }[]; // { date, pos } per finish, chronological (oldest → newest)
}

interface IndividualPerformanceRecord {
  player_id: number;
  first_name: string;
  last_name: string;
  regular_gross_average: number | null;
  regular_net_average: number | null;
  major_gross_average: number | null;
  major_net_average: number | null;
  combined_gross_average: number | null;
  combined_net_average: number | null;
}

interface Season {
  season_id: number;
  season_year: string;
}

const s = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  } as React.CSSProperties,
  // Centered content column that fills small screens and caps on large ones.
  inner: {
    maxWidth: 1120,
    margin: '0 auto',
    width: '100%',
  } as React.CSSProperties,
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: 12,
    marginBottom: 28,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  },
  seasonWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  seasonLabel: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: 500,
  },
  seasonSelect: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 14,
    color: '#1e293b',
    background: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    border: '1px solid #e2e8f0',
    marginBottom: 24,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  },
  rankBadge: (rank: number): React.CSSProperties => {
    const colors: Record<number, { bg: string; color: string }> = {
      1: { bg: '#fef3c7', color: '#92400e' },
      2: { bg: '#f1f5f9', color: '#475569' },
      3: { bg: '#fef0e7', color: '#9a3412' },
    };
    const c = colors[rank] || { bg: '#f8fafc', color: '#94a3b8' };
    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: c.bg,
      color: c.color,
      fontWeight: 700,
      fontSize: 13,
    };
  },
  rankEmoji: (rank: number) => rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null,
  playerName: {
    fontWeight: 600,
    color: '#1e293b',
  },
  finishBadge: (count: number): React.CSSProperties => ({
    display: 'inline-block',
    minWidth: 24,
    padding: '2px 6px',
    borderRadius: 6,
    background: count > 0 ? '#dbeafe' : '#f1f5f9',
    color: count > 0 ? '#1d4ed8' : '#94a3b8',
    fontWeight: count > 0 ? 700 : 400,
    fontSize: 13,
    textAlign: 'center',
  }),
  pctText: {
    fontSize: 11,
    color: '#94a3b8',
    marginLeft: 4,
  },
  avgValue: (val: number | null): React.CSSProperties => ({
    fontWeight: val !== null ? 600 : 400,
    color: val !== null ? '#1e293b' : '#94a3b8',
  }),
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center' as const,
    color: '#94a3b8',
    fontSize: 14,
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '80px 20px',
    color: '#64748b',
    fontSize: 16,
  },
  // Responsive leaderboard rows
  rowList: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  lbRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 20px',
    borderBottom: '1px solid #f1f5f9',
  },
  lbMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
  },
  lbTopLine: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap' as const,
  },
  lbName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e293b',
  },
  lbTotal: {
    fontSize: 20,
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.02em',
    whiteSpace: 'nowrap' as const,
  },
  lbTotalUnit: {
    fontSize: 12,
    fontWeight: 600,
    color: '#94a3b8',
    marginLeft: 3,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 4,
    background: '#f1f5f9',
    borderRadius: 6,
    padding: '3px 9px',
    fontSize: 12,
    whiteSpace: 'nowrap' as const,
  },
  chipLabel: {
    color: '#94a3b8',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    fontSize: 10,
  },
  chipVal: {
    color: '#475569',
    fontWeight: 700,
  },
};

// Scoped styles for the Finishes card (table on desktop, cards on mobile).
const finishesCss = `
.fin-seg{display:inline-flex;background:#f1f5f9;border-radius:10px;padding:4px;gap:4px}
.fin-seg button{border:none;background:transparent;color:#64748b;font-weight:700;font-size:13px;padding:7px 14px;border-radius:8px;cursor:pointer}
.fin-seg button.active{background:#fff;color:#1e40af;box-shadow:0 1px 3px rgba(0,0,0,.12)}
.fin-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;padding:12px 18px;border-bottom:1px solid #f1f5f9;background:#fcfdff}
.fin-hint{font-size:12px;color:#64748b}
.fin-search{border:1px solid #e2e8f0;border-radius:8px;padding:7px 11px;font-size:13px;min-width:150px;outline:none}
.fin-tbl{border-collapse:separate;border-spacing:0}
.fin-tbl th{position:sticky;top:0;background:#f8fafc;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.05em;border-bottom:1px solid #e2e8f0}
.fin-tbl th.r,.fin-tbl td.r{text-align:center}
.fin-tbl td{border-bottom:1px solid #f1f5f9;vertical-align:middle}
.fin-tbl tbody tr:nth-child(even){background:#fcfdff}
.fin-tbl tbody tr:hover{background:#f5f9ff}
.fin-name{font-weight:700;color:#1e293b}
.fin-played{font-weight:700;color:#1e293b}
.fin-col{display:inline-flex;flex-direction:column;align-items:center;line-height:1.15}
.fin-pctx{font-size:.72em;font-size:max(.72em,8px);color:#94a3b8}
.fin-zero{color:#cbd5e1;font-weight:600}
.fin-pill{display:inline-block;min-width:2em;padding:.2em .55em;border-radius:8px;font-weight:800}
.fin-p1{background:#fef3c7;color:#b45309}.fin-p2{background:#f1f5f9;color:#475569}.fin-p3{background:#fef0e7;color:#9a3412}.fin-pr{background:#dbeafe;color:#1d4ed8}
.fin-dq{background:#fef2f2;color:#dc2626}
.fin-top3{font-weight:800;color:#1e293b}
.fin-form{display:inline-flex;gap:3px;flex-wrap:wrap;justify-content:center}
.fin-dot{display:inline-flex;align-items:center;justify-content:center;min-width:1.7em;height:1.7em;border-radius:6px;font-size:.8em;font-weight:800;padding:0 .4em}
.fin-dot.last{box-shadow:0 0 0 2px #fff, 0 0 0 3px #cbd5e1}
.fin-dot.empty{background:transparent;border:1px dashed #e2e8f0}
.fin-cards{display:none;flex-direction:column}
.fin-prow{display:flex;flex-direction:column;gap:8px;padding:14px 18px;border-bottom:1px solid #f1f5f9}
.fin-ptop{display:flex;align-items:center;justify-content:space-between;gap:10px}
.fin-chips{display:flex;flex-wrap:wrap;gap:6px}
.fin-chip{display:inline-flex;align-items:baseline;gap:5px;border-radius:999px;padding:3px 9px;font-size:12px;font-weight:800}
.fin-chip small{font-weight:700;opacity:.75}
.fin-c1{background:#fef3c7;color:#b45309}.fin-c2{background:#f1f5f9;color:#475569}.fin-c3{background:#fef0e7;color:#9a3412}.fin-cr{background:#dbeafe;color:#1d4ed8}
.fin-empty{color:#94a3b8;font-size:14px;padding:28px 18px;text-align:center}
.fin-emptychips{color:#cbd5e1;font-size:12px;font-weight:600}
@media (max-width:640px){.fin-tablewrap{display:none}.fin-cards{display:flex}.fin-hint{display:none}}
.bk-row:hover{background:#f5f9ff}
.bk-panel{background:#f8fafc;border-top:1px solid #eef2f7;padding:10px 14px 14px}
.bk-tbl{--fit-base:13px}
.bk-tbl th{background:transparent;color:#64748b;letter-spacing:.04em;padding:.45em .75em;border-bottom:1px solid #e2e8f0}
.bk-tbl th.r,.bk-tbl td.r{text-align:right}
.bk-tbl th.c,.bk-tbl td.c{text-align:center}
.bk-tbl td{padding:.55em .75em;border-bottom:1px solid #eef2f7;color:#334155}
.bk-date{color:#64748b}
.bk-major td{background:#fffdf5}
.bk-tag{display:inline-block;font-size:.77em;font-weight:800;color:#b45309;background:#fef3c7;border-radius:6px;padding:.1em .45em;margin-right:.45em;vertical-align:middle}
.bk-tbl tfoot td{padding:.7em .8em;font-weight:800;color:#0f172a;border-top:2px solid #e2e8f0}
.bk-empty{color:#94a3b8;font-size:13px;padding:8px 2px}
`;

const medalFor = (r: number) => (r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : `#${r}`);
const fmtDate = (d: string) => {
  const m = (d || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return d || '—';
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-CA', { year: '2-digit', month: 'short', day: 'numeric' });
};
const pillClassFor = (r: number) => (r === 1 ? 'fin-p1' : r === 2 ? 'fin-p2' : r === 3 ? 'fin-p3' : 'fin-pr');
const chipClassFor = (r: number) => (r === 1 ? 'fin-c1' : r === 2 ? 'fin-c2' : r === 3 ? 'fin-c3' : 'fin-cr');

const Chip: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <span style={s.chip}>
    <span style={s.chipLabel}>{label}</span>
    <span style={s.chipVal}>{value}</span>
  </span>
);

const Standings: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const [standings, setStandings] = useState<StandingRecord[]>([]);
  const [expandedPlayer, setExpandedPlayer] = useState<number | null>(null);
  const [regFinishes, setRegFinishes] = useState<RegFinishesRecord[]>([]);
  const [individualPerformance, setIndividualPerformance] = useState<IndividualPerformanceRecord[]>([]);
  const [majorFinishes, setMajorFinishes] = useState<MajorFinishRow[]>([]);
  const [finishesMode, setFinishesMode] = useState<'all' | 'regular' | 'major'>('all');
  const [scoringMode, setScoringMode] = useState<'gross' | 'net'>('gross');
  const [playerFilter, setPlayerFilter] = useState<string>('');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const APIURL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get<Season[]>(`${APIURL}/seasons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSeasons(response.data);
        if (response.data.length > 0) {
          setSelectedSeason(response.data[0].season_year);
        }
      } catch {
        setError('Failed to fetch seasons.');
      }
    };
    if (isAuthenticated) {
      fetchSeasons();
    } else if (!isLoading) {
      loginWithRedirect();
    }
  }, [getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, APIURL]);

  useEffect(() => {
    if (!selectedSeason) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const [standingsRes, regFinishesRes, majorFinishesRes, perfRes] = await Promise.all([
          axios.get<StandingRecord[]>(`${APIURL}/standings?season_year=${selectedSeason}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get<RegFinishesRecord[]>(`${APIURL}/regfinishes?season_year=${selectedSeason}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get<{ max_rank: number; rows: MajorFinishRow[] }>(`${APIURL}/majorfinishes?season_year=${selectedSeason}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get<IndividualPerformanceRecord[]>(`${APIURL}/averagescores?season_year=${selectedSeason}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setStandings(standingsRes.data);
        setRegFinishes(regFinishesRes.data);
        setMajorFinishes(majorFinishesRes.data.rows || []);
        setIndividualPerformance(perfRes.data);
        setError(null);
      } catch {
        setError('Failed to fetch data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedSeason, getAccessTokenSilently, APIURL]);

  const pct = (n: number, total: number) =>
    total > 0 ? `${((n / total) * 100).toFixed(0)}%` : '—';

  // Unified finishes view: All (combined), regular-season per-round finishes, or
  // per-major final placements, filtered by the player search box.
  // Counts arrive from SQL as bigint → strings, so coerce everything to Number
  // (otherwise sums become string concatenations and percentages blow up).
  // Each recent finish carries its date so the combined "All" view can
  // interleave regular + major finishes into one chronological streak.
  type RecentEntry = { date: string; pos: number | string };
  type FinishRow = { player_id: number; name: string; played: number; finishes: number[]; dq: number; recent: RecentEntry[] };

  const toRecent = (raw: unknown): RecentEntry[] =>
    (Array.isArray(raw) ? raw : [])
      .map((v): RecentEntry => (v && typeof v === 'object'
        ? { date: String((v as RecentEntry).date ?? ''), pos: (v as RecentEntry).pos === 'DQ' ? 'DQ' : Number((v as RecentEntry).pos) }
        : { date: '', pos: v === 'DQ' ? 'DQ' : Number(v) })); // tolerate the old bare-number shape

  const regRows: FinishRow[] = regFinishes.map((r) => ({
    player_id: r.player_id,
    name: `${r.first_name} ${r.last_name}`,
    played: Number(r.regular_season_starts),
    finishes: [r.rank_1_finishes, r.rank_2_finishes, r.rank_3_finishes, r.rank_4_finishes, r.rank_5_finishes, r.rank_6_finishes, r.rank_7_finishes].map(Number),
    dq: Number(r.dq_finishes) || 0,
    recent: toRecent(r.recent),
  }));
  const majorRows: FinishRow[] = majorFinishes.map((r) => ({
    player_id: r.player_id,
    name: `${r.first_name} ${r.last_name}`,
    played: Number(r.majors_played),
    finishes: (r.finishes || []).map(Number),
    dq: 0,
    recent: toRecent(r.recent),
  }));

  // "All" sums each player's regular + major finishes position-by-position, and
  // merges their two recent streaks by date so the Last-5 form strip reflects
  // the player's true most-recent events across both.
  const allRows: FinishRow[] = (() => {
    const byId = new Map<number, FinishRow>();
    for (const r of regRows) byId.set(r.player_id, { ...r, finishes: [...r.finishes], recent: [...r.recent] });
    for (const m of majorRows) {
      const cur = byId.get(m.player_id);
      if (!cur) { byId.set(m.player_id, { ...m, finishes: [...m.finishes], recent: [...m.recent] }); continue; }
      cur.played += m.played;
      const len = Math.max(cur.finishes.length, m.finishes.length);
      const merged: number[] = [];
      for (let i = 0; i < len; i++) merged[i] = (cur.finishes[i] || 0) + (m.finishes[i] || 0);
      cur.finishes = merged;
      cur.recent = [...cur.recent, ...m.recent];
    }
    // Oldest → newest, so the form strip and its "last" ring stay correct.
    const out = Array.from(byId.values());
    out.forEach((row) => row.recent.sort((a, b) => a.date.localeCompare(b.date)));
    return out;
  })();

  const finishRows = (finishesMode === 'all' ? allRows : finishesMode === 'regular' ? regRows : majorRows)
    .filter((r) => r.name.toLowerCase().includes(playerFilter.trim().toLowerCase()));

  // Trim trailing all-zero rank columns so the table only shows ranks that occur.
  let finMaxRank = 0;
  for (const r of finishRows) {
    for (let i = r.finishes.length - 1; i >= 0; i--) {
      if ((r.finishes[i] || 0) > 0) { if (i + 1 > finMaxRank) finMaxRank = i + 1; break; }
    }
  }
  if (finMaxRank === 0) finMaxRank = Math.min(3, finishRows[0]?.finishes.length || 3);
  const finRanks = Array.from({ length: finMaxRank }, (_, i) => i + 1);
  const finUnit = finishesMode === 'all' ? 'Events' : finishesMode === 'regular' ? 'Starts' : 'Majors';

  // "Last 5" form guide: the player's finishing positions in their 5 most recent
  // events, oldest → newest, podium-coloured (the most recent is ringed).
  const renderForm = (recent: RecentEntry[]) => {
    if (recent.length === 0) return <span className="fin-zero">—</span>;
    const last5 = recent.slice(-5).map((e) => e.pos);
    const pad = 5 - last5.length; // always show a 5-slot strip; pad older/missing slots
    return (
      <span className="fin-form">
        {Array.from({ length: pad }).map((_, i) => <span key={`e${i}`} className="fin-dot empty" />)}
        {last5.map((pos, i) => {
          const isDq = pos === 'DQ';
          const lastCls = i === last5.length - 1 ? ' last' : '';
          return isDq
            ? <span key={i} className={`fin-dot fin-dq${lastCls}`} title="Disqualified">DQ</span>
            : <span key={i} className={`fin-dot ${pillClassFor(pos as number)}${lastCls}`} title={`#${pos}`}>{pos}</span>;
        })}
      </span>
    );
  };

  if (isLoading || loading) return <div style={s.loadingWrap}>Loading standings…</div>;
  if (error) return <div style={{ padding: 24, color: '#ef4444' }}>{error}</div>;

  return (
    <div style={s.page}>
      <style>{finishesCss}</style>
      <div style={s.inner}>
      <div style={s.topBar}>
        <h1 style={s.pageTitle}>Standings</h1>
        <div style={s.seasonWrap}>
          <span style={s.seasonLabel}>Season</span>
          <select
            style={s.seasonSelect}
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
          >
            {seasons.map((season) => (
              <option key={season.season_id} value={season.season_year}>
                {season.season_year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Points Leaderboard */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={{ fontSize: 20 }}>🏆</span>
          <h2 style={s.cardTitle}>Points Leaderboard</h2>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
            Click on a player to view their points log
          </span>
        </div>
        {standings.length === 0 ? (
          <div style={s.emptyState}>No standings data for this season.</div>
        ) : (
          <div style={s.rowList}>
            {standings.map((record, i) => {
              const isOpen = expandedPlayer === record.player_id;
              const bd = record.breakdown || [];
              const bdSum = bd.reduce((a, e) => a + Number(e.points), 0);
              return (
                <div key={record.player_id}>
                  <div
                    className="bk-row"
                    style={{ ...s.lbRow, cursor: 'pointer', ...(isOpen ? { background: '#eff4fb' } : i === 0 ? { background: '#fffbeb' } : {}) }}
                    onClick={() => setExpandedPlayer(isOpen ? null : record.player_id)}
                    title="Show points breakdown"
                  >
                    <span style={s.rankBadge(i + 1)}>{i + 1}</span>
                    <div style={s.lbMain}>
                      <div style={s.lbTopLine}>
                        <span style={s.lbName}>{record.first_name} {record.last_name}</span>
                        <span style={{ ...s.lbTotal, color: i === 0 ? '#92400e' : '#0f172a' }}>
                          {record.total_points}<span style={s.lbTotalUnit}>pts</span>
                        </span>
                      </div>
                      <div style={s.chipRow}>
                        <Chip label="Reg" value={record.total_round_points} />
                        <Chip label="Reg Team" value={record.total_round_team_points} />
                        <Chip label="Major" value={record.total_tournament_points} />
                        <Chip label="Major Team" value={record.total_tournament_team_points} />
                      </div>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="bk-panel">
                      {bd.length === 0 ? (
                        <div className="bk-empty">No point-earning events this season.</div>
                      ) : (
                        <FitTable className="bk-wrap">
                          <table className="tbl tbl--sm bk-tbl">
                            <thead>
                              <tr><th>Date</th><th>Course / Event</th><th className="c">Finish</th><th className="r">Points</th></tr>
                            </thead>
                            <tbody>
                              {bd.map((e, idx) => (
                                <tr key={idx} className={e.kind === 'major' ? 'bk-major' : ''}>
                                  <td className="bk-date">{fmtDate(e.date)}</td>
                                  <td>
                                    {e.kind === 'major' && <span className="bk-tag">🏆 Major</span>}
                                    {e.kind === 'round_bonus' && <span className="bk-tag">🎯 Team bonus</span>}
                                    {e.course}
                                  </td>
                                  <td className="c">{e.position == null ? '—' : medalFor(Number(e.position))}</td>
                                  <td className="r">{Number(e.points).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={3}>Total points</td>
                                <td className="r">{bdSum.toFixed(2)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </FitTable>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Finishes (Regular Season / Majors) */}
      <div style={{ ...s.card, border: 'none' }}>
        <div style={s.cardHeader}>
          <span style={{ fontSize: 20 }}>📊</span>
          <h2 style={s.cardTitle}>Finishes</h2>
          <div className="fin-seg" style={{ marginLeft: 'auto' }}>
            <button className={finishesMode === 'all' ? 'active' : ''} onClick={() => setFinishesMode('all')}>All</button>
            <button className={finishesMode === 'regular' ? 'active' : ''} onClick={() => setFinishesMode('regular')}>Season</button>
            <button className={finishesMode === 'major' ? 'active' : ''} onClick={() => setFinishesMode('major')}>Majors</button>
          </div>
        </div>
        <div className="fin-toolbar">
          <span className="fin-hint">
            {finishesMode === 'all'
              ? 'Finishes across all events — rounds and majors combined.'
              : finishesMode === 'regular'
                ? 'Finishing position per regular-season round.'
                : 'Final placement in each major (🥇 = wins).'}
          </span>
          <input
            className="fin-search"
            placeholder="Filter players…"
            value={playerFilter}
            onChange={(e) => setPlayerFilter(e.target.value)}
          />
        </div>
        {finishRows.length === 0 ? (
          <div className="fin-empty">No {finishesMode === 'major' ? 'major' : finishesMode === 'regular' ? 'regular-season' : ''} finishes for this season.</div>
        ) : (
          <>
            {/* Desktop table */}
            <FitTable className="fin-tablewrap">
              <table className="tbl fin-tbl">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Player</th>
                    <th className="r">{finUnit}</th>
                    {finRanks.map((r) => <th key={r} className="r">{medalFor(r)}</th>)}
                    {finishesMode !== 'major' && <th className="r">DQ</th>}
                    <th className="r">Top 3</th>
                    <th className="r">Last 5</th>
                  </tr>
                </thead>
                <tbody>
                  {finishRows.map((p) => {
                    const top3 = (p.finishes[0] || 0) + (p.finishes[1] || 0) + (p.finishes[2] || 0);
                    return (
                      <tr key={p.player_id}>
                        <td className="tbl-name"><PlayerName full={p.name} className="fin-name" /></td>
                        <td className="r"><span className="fin-played">{p.played}</span></td>
                        {finRanks.map((r, i) => {
                          const c = p.finishes[i] || 0;
                          return (
                            <td key={r} className="r">
                              {c > 0 ? (
                                <span className="fin-col">
                                  <span className={`fin-pill ${pillClassFor(r)}`}>{c}</span>
                                  <span className="fin-pctx">{pct(c, p.played)}</span>
                                </span>
                              ) : <span className="fin-zero">·</span>}
                            </td>
                          );
                        })}
                        {finishesMode !== 'major' && (
                          <td className="r">
                            {p.dq > 0 ? (
                              <span className="fin-col">
                                <span className="fin-pill fin-dq">{p.dq}</span>
                                <span className="fin-pctx">{pct(p.dq, p.played)}</span>
                              </span>
                            ) : <span className="fin-zero">·</span>}
                          </td>
                        )}
                        <td className="r"><span className="fin-top3">{top3}</span> <span className="fin-pctx">{pct(top3, p.played)}</span></td>
                        <td className="r">{renderForm(p.recent)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </FitTable>
            {/* Mobile cards */}
            <div className="fin-cards">
              {finishRows.map((p) => {
                const anyFin = p.finishes.some((c) => (c || 0) > 0);
                return (
                  <div key={p.player_id} className="fin-prow">
                    <div className="fin-ptop">
                      <span className="fin-name">{p.name}</span>
                      <span style={{ fontSize: 13, color: '#64748b' }}>{p.played} {finUnit.toLowerCase()}</span>
                    </div>
                    <div className="fin-chips">
                      {anyFin ? finRanks.map((r, i) => (p.finishes[i] || 0) > 0
                        ? <span key={r} className={`fin-chip ${chipClassFor(r)}`}>{medalFor(r)} {p.finishes[i]} <small>{pct(p.finishes[i], p.played)}</small></span>
                        : null)
                        : <span className="fin-emptychips">No finishes recorded</span>}
                      {finishesMode !== 'major' && p.dq > 0 && (
                        <span className="fin-chip fin-dq">DQ {p.dq} <small>{pct(p.dq, p.played)}</small></span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>Last 5</span>
                      {renderForm(p.recent)}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Individual Performance */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={{ fontSize: 20 }}>⛳</span>
          <h2 style={s.cardTitle}>Scoring Averages</h2>
          <div className="fin-seg" style={{ marginLeft: 'auto' }}>
            <button className={scoringMode === 'gross' ? 'active' : ''} onClick={() => setScoringMode('gross')}>Gross</button>
            <button className={scoringMode === 'net' ? 'active' : ''} onClick={() => setScoringMode('net')}>Net</button>
          </div>
        </div>
        {(() => {
          const pick = (r: IndividualPerformanceRecord, kind: 'regular' | 'major' | 'combined') =>
            scoringMode === 'gross'
              ? (kind === 'regular' ? r.regular_gross_average : kind === 'major' ? r.major_gross_average : r.combined_gross_average)
              : (kind === 'regular' ? r.regular_net_average : kind === 'major' ? r.major_net_average : r.combined_net_average);
          // Only show players who actually played — no rounds means every average is null.
          const played = [...individualPerformance].filter(
            (r) => pick(r, 'combined') !== null || pick(r, 'regular') !== null || pick(r, 'major') !== null
          );
          return played.length === 0 ? (
            <div style={s.emptyState}>No performance data for this season.</div>
          ) : (
            <div style={s.rowList}>
              {played
                .sort((a, b) => (pick(a, 'combined') ?? 999) - (pick(b, 'combined') ?? 999))
                .map((record) => {
                  const combined = pick(record, 'combined');
                  return (
                    <div key={record.player_id} style={s.lbRow}>
                      <div style={s.lbMain}>
                        <div style={s.lbTopLine}>
                          <span style={s.lbName}>{record.first_name} {record.last_name}</span>
                          <span style={{ ...s.lbTotal, color: combined !== null ? '#0f172a' : '#cbd5e1' }}>
                            {combined ?? '—'}
                            <span style={s.lbTotalUnit}>{scoringMode} avg</span>
                          </span>
                        </div>
                        <div style={s.chipRow}>
                          <Chip label="Season" value={pick(record, 'regular') ?? '—'} />
                          <Chip label="Majors" value={pick(record, 'major') ?? '—'} />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })()}
      </div>
      </div>
    </div>
  );
};

export default Standings;
